import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { TikTokIcon } from "@/components/TikTokSection";

export default function AdminTikTok() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tiktok");
      setVideos(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/tiktok", { video_url: url.trim() });
      toast.success("TikTok video added");
      setUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this video?")) return;
    try {
      await api.delete(`/admin/tiktok/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-tiktok">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Social</div>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">TikTok videos</h1>
        <p className="text-white/60 text-sm mt-2">Paste a full TikTok video link — it embeds in the "Watch the breaks live" section on the homepage.</p>
      </div>

      <form onSubmit={add} className="ufr-card rounded-md p-6 mb-8" data-testid="tiktok-form">
        <div className="flex items-center gap-2 mb-3">
          <Plus size={16} className="text-[color:var(--ufr-green)]" />
          <div className="text-sm font-bold text-white uppercase tracking-wider">Add a video</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="flex-1 bg-black/50 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[color:var(--ufr-green)] outline-none"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.tiktok.com/@ultimatefootballbreaks/video/123456789"
            data-testid="tiktok-url-input"
          />
          <button type="submit" disabled={saving} className="btn-ufr-primary px-6 py-3 text-sm" data-testid="tiktok-add-btn">
            {saving ? "Adding…" : "Add video"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="ufr-card rounded-md p-10 text-center">
          <TikTokIcon size={32} className="mx-auto text-white/30" />
          <div className="font-display text-2xl text-white mt-3">No videos yet</div>
          <p className="text-white/60 text-sm mt-2">Add your first TikTok video above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tiktok-admin-grid">
          {videos.map((v) => (
            <div key={v.id} className="ufr-card rounded-md overflow-hidden relative" data-testid={`tiktok-admin-tile-${v.id}`}>
              <iframe
                src={`https://www.tiktok.com/player/v1/${v.video_id}?loop=1&rel=0&autoplay=0&music_info=0&description=0`}
                title="TikTok"
                className="w-full"
                style={{ height: 500, border: "none" }}
                loading="lazy"
              />
              <button
                onClick={() => remove(v.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/80 text-white/80 hover:text-red-400 flex items-center justify-center"
                data-testid={`tiktok-delete-${v.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
